import { useState } from 'react'
import { api, ApiError, session } from '../lib/api'
import { useApi } from '../lib/hooks'
import { clock, dateTime, maskedPhone, money, num, titleCase } from '../lib/format'
import { Banner, Empty, ErrorNote, Kpi, Loading, Panel, Pill, StatMini } from '../ui/kit'

/**
 * Money out. The promise the whole business is sold on is that a farmer is paid the
 * day they deliver, before the cutoff — so this screen exists to make the run a
 * deliberate act by a named person, and to show plainly what has not gone yet.
 */

interface Summary {
  totalAmount: number; count: number
  queued: number; inFlight: number; confirmed: number; failed: number
  settledAmount: number; outstandingAmount: number
  settledPct: number | null; cutoff: string; paidBeforeCutoffPct: number | null
}

interface Payment {
  id: string; status: string; amount: number; currency: string; channel: string
  accountRef: string; accountName: string; providerReceipt: string | null
  requestedAt: string; settledAt: string | null
  failureCode: string | null; failureReason: string | null
  retryCount: number; nextRetryAt: string | null
  farmerName: string; farmerCode: string
  lotCode: string | null; lotKg: number | null
  batchCode: string | null; requestedByName: string | null
}

const TONE: Record<string, 'ok' | 'warn' | 'crit' | 'info' | 'neutral'> = {
  queued: 'warn', authorised: 'info', sent: 'info', confirmed: 'ok',
  failed: 'crit', blocked_duplicate: 'crit', reversed: 'neutral',
}

export function Payouts() {
  // Authorising a run moves real money, so it is finance's. Requeueing a failure is
  // ops housekeeping. The server draws the same line.
  const canRun = session.has('admin', 'finance')
  const canRetry = session.has('admin', 'ops')

  const [status, setStatus] = useState('')
  const summary = useApi<Summary>('/payments/summary')
  const payments = useApi<Payment[]>(`/payments${status ? `?status=${status}` : ''}`, { deps: [status] })

  const [banner, setBanner] = useState<{ tone: 'ok' | 'crit'; text: string } | null>(null)
  const [running, setRunning] = useState(false)

  const s = summary.data
  const rows = payments.data ?? []
  const failed = rows.filter((p) => p.status === 'failed')

  function reload() {
    summary.reload()
    payments.reload()
  }

  async function run() {
    if (!s || s.queued === 0) return
    const question =
      `Pay ${s.queued} farmer${s.queued === 1 ? '' : 's'} a total of ` +
      `${money(s.outstandingAmount)}?\n\n` +
      'This goes to M-Pesa under your name, and cannot be taken back once sent.'
    if (!window.confirm(question)) return

    setRunning(true)
    try {
      const res = await api.post<{
        code: string; itemCount: number; totalAmount: number
      }>('/payments/run')
      setBanner({
        tone: 'ok',
        text: `Sent — ${res.itemCount} payment${res.itemCount === 1 ? '' : 's'}, ` +
          `${money(res.totalAmount)} (${res.code}). M-Pesa confirms each one back.`,
      })
      reload()
    } catch (e) {
      setBanner({ tone: 'crit', text: e instanceof ApiError ? e.message : 'The payments did not go through.' })
    } finally {
      setRunning(false)
    }
  }

  async function retry(p: Payment) {
    try {
      await api.post(`/payments/${p.id}/retry`)
      setBanner({ tone: 'ok', text: `${p.farmerName} will be paid on the next run.` })
      reload()
    } catch (e) {
      setBanner({ tone: 'crit', text: e instanceof ApiError ? e.message : 'That did not work.' })
    }
  }

  return (
    <main className="page">
      <div className="page-head">
        <h1>Paying farmers</h1>
        <p>
          You promised to pay the same day, by {s ? clock(s.cutoff) : '13:00'}. Everyone waiting below
          has already handed over their produce.
        </p>
      </div>

      <div className="stack">
        {banner && (
          <Banner tone={banner.tone === 'ok' ? 'info' : 'crit'} title={banner.text}>{null}</Banner>
        )}

        {summary.error && <ErrorNote error={summary.error} onRetry={summary.reload} />}
        {summary.loading && <Panel><Loading rows={2} /></Panel>}

        {s && (
          <>
            <div className="kpi-grid">
              <Kpi
                label="Waiting to be paid"
                value={s.queued}
                sub={<>{money(s.outstandingAmount, { compact: true })} owed to farmers right now</>}
                tone={s.queued > 0 ? 'warn' : 'ok'}
              />
              <Kpi
                label="Settled"
                value={s.settledPct ?? '—'}
                unit={s.settledPct === null ? undefined : '%'}
                sub={<>{money(s.settledAmount, { compact: true })} of {money(s.totalAmount, { compact: true })}</>}
                meterPct={s.settledPct}
                tone={s.settledPct === null ? 'neutral' : s.settledPct >= 95 ? 'ok' : 'warn'}
              />
              <Kpi
                label={`Paid before ${clock(s.cutoff)}`}
                value={s.paidBeforeCutoffPct ?? '—'}
                unit={s.paidBeforeCutoffPct === null ? undefined : '%'}
                sub={<>this is what farmers judge you on</>}
                meterPct={s.paidBeforeCutoffPct}
                tone={s.paidBeforeCutoffPct === null ? 'neutral'
                  : s.paidBeforeCutoffPct >= 95 ? 'ok' : 'crit'}
              />
              <Kpi
                label="Failed"
                value={s.failed}
                sub={s.failed > 0
                  ? <>somebody is owed money and does not know why</>
                  : <>nothing bounced back</>}
                tone={s.failed > 0 ? 'crit' : 'ok'}
              />
            </div>

            {s.queued > 0 && (
              <Panel>
                <div className="panel-body spread" style={{ gap: 'var(--sp-7)', flexWrap: 'wrap' }}>
                  <div>
                    <b style={{ fontSize: 'var(--fs-lg)' }}>
                      {s.queued} payment{s.queued === 1 ? '' : 's'} queued · {money(s.outstandingAmount)}
                    </b>
                    <p className="hint" style={{ marginTop: 2 }}>
                      {canRun
                        ? 'Your name goes on this, and it goes straight to M-Pesa.'
                        : 'Only finance can send these. Ask them if it is getting late.'}
                    </p>
                  </div>
                  {canRun && (
                    <button className="btn lg" disabled={running} onClick={() => void run()}>
                      {running ? 'Sending…' : 'Pay them now'}
                    </button>
                  )}
                </div>
              </Panel>
            )}

            {failed.length > 0 && (
              <Banner tone="crit" title={`${failed.length} payment(s) failed and nobody has told the farmer`}>
                This is worse than being late. They handed over produce, nothing arrived, and they
                have no way to ask. Try again or fix the M-Pesa details — then ring them.
              </Banner>
            )}
          </>
        )}

        <Panel
          title="Payments"
          meta={
            <select className="input" style={{ minHeight: 30, width: 'auto' }}
              value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All statuses</option>
              <option value="queued">Waiting to be sent</option>
              <option value="authorised">Approved</option>
              <option value="sent">Sent to M-Pesa</option>
              <option value="confirmed">Paid</option>
              <option value="failed">Failed</option>
            </select>
          }
          footer={
            <>
              <StatMini label="Shown" value={rows.length} />
              <StatMini label="Value"
                value={money(rows.reduce((a, p) => a + Number(p.amount ?? 0), 0), { compact: true })} />
              <StatMini label="Failed" value={failed.length} />
            </>
          }
        >
          {payments.error && <ErrorNote error={payments.error} onRetry={payments.reload} />}
          {payments.loading && <Loading rows={5} />}
          {payments.data?.length === 0 && (
            <Empty title="Nothing here">
              A payment is created by the agent at the farm, the moment produce is weighed.
            </Empty>
          )}
          {rows.length > 0 && (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Farmer</th><th>M-Pesa</th><th>For</th>
                    <th className="n">Amount</th><th>Status</th>
                    <th>M-Pesa code</th><th className="n">Raised</th>
                    {canRetry && <th style={{ width: 80 }} />}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((p) => (
                    <tr key={p.id} className={p.status === 'failed' ? 'attention' : ''}>
                      <td className="truncate">
                        <b>{p.farmerName}</b>
                        <span className="faint code"> {p.farmerCode}</span>
                      </td>
                      <td className="mono" style={{ fontSize: 'var(--fs-xs)' }}>
                        {maskedPhone(p.accountRef)}
                        <div className="faint">{p.accountName}</div>
                      </td>
                      <td className="code faint">
                        {p.lotCode ?? '—'}
                        {p.lotKg !== null && <span className="faint"> · {num(p.lotKg, 1)} kg</span>}
                      </td>
                      <td className="n strong">{money(p.amount, { currency: false })}</td>
                      <td>
                        <Pill tone={TONE[p.status] ?? 'neutral'}>{titleCase(p.status)}</Pill>
                        {p.failureReason && (
                          <div className="crit-t" style={{ fontSize: 'var(--fs-2xs)' }}>{p.failureReason}</div>
                        )}
                      </td>
                      <td className="mono faint" style={{ fontSize: 'var(--fs-xs)' }}>
                        {p.providerReceipt ?? '—'}
                      </td>
                      <td className="n faint">{dateTime(p.requestedAt)}</td>
                      {canRetry && (
                        <td style={{ textAlign: 'right' }}>
                          {p.status === 'failed' && (
                            <button className="btn quiet" style={tinyBtn} onClick={() => void retry(p)}>
                              Try again
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>

        <Banner tone="info" title="Nobody can be paid twice for the same produce">
          Each payment is tied to one collection, and the system will not allow a second one against
          it — however many times a phone with no signal retries, or somebody taps the button again.
          That rule sits underneath this screen and cannot be got around from here.
        </Banner>
      </div>
    </main>
  )
}

const tinyBtn = { minHeight: 24, padding: '1px 8px', fontSize: 'var(--fs-2xs)' } as const
